import { hideLoading, showLoading } from 'react-redux-loading-bar';
import omit from 'lodash/omit';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import { normalize } from 'normalizr';
import dissoc from 'ramda/src/dissoc';
import when from 'ramda/src/when';
import prop from 'ramda/src/prop';
import pipe from 'ramda/src/pipe';
import pipeP from 'ramda/src/pipeP';
import tap from 'ramda/src/tap';
import { isPropFileInstance } from '../../common/helpers/functional';
import Http from '../../http';
import {
  UPDATE_SHOP,
  GET_SHOP,
  ADD_SHOP_PHOTO,
  SHOP_FETCH_SUBSCRIPTION,
  UPLOAD_SHOP_LOGO,
  GET_SHOPS,
} from './constants';
import * as schema from '../../admin/shops/schema';
import countShopBarbers from '../../common/helpers/countShopBarbers';

const uploadShopLogoAction = () => ({ type: UPLOAD_SHOP_LOGO });
const uploadShopLogo = (shopId, image) => dispatch => pipeP(
  Http.postRaw.bind(Http, `/shop/${shopId}/logo`, { image }),
  uploadShopLogoAction,
  dispatch
)();

export function addShopImage(shopId, image) {
  return dispatch => Http.postRaw(`/shop/${shopId}/photo`, {
    image,
    enabled: true,
    order: 0,
  }).then((photo) => {
    dispatch({ type: ADD_SHOP_PHOTO, photo, shopId });
    return photo;
  });
}

export function changeShopImage(shopId, oldImage, newImage) {
  return (dispatch, getState) => Http.delete(`/shop/${shopId}/photo/${oldImage.id}`)
    .then(() => addShopImage(shopId, newImage)(dispatch, getState));
}

export function refreshShopData(shopId) {
  return dispatch =>
    Http.get(
      `/shop/${shopId}`,
      { include: 'address,photos,logo,schedules,barbers,users,brand,days_off' }
    )
    .then((shop) => {
      dispatch({
        type: UPDATE_SHOP,
        shop,
        response: normalize(countShopBarbers(shop), schema.shop),
      });
      return shop;
    });
}

export function updateShop(shopId, data, currentShopData) {
  return (dispatch) => {
    const stripMediaProps = obj =>
      ['avatar', 'logo'].reduce((acc, key) => {
        if (isPropFileInstance(key, acc)) return dissoc(key, acc);
        return acc;
      }, { ...obj });
    const cleanHeading = when(
      pipe(prop('heading'), isNil),
      dissoc('heading')
    );
    const prepareData = pipe(stripMediaProps, cleanHeading);

    const getMediaFile = key => ((data[key] instanceof File) ? data[key] : null);
    const uploadImage = () => {
      const avatar = getMediaFile('avatar');
      if (!avatar) {
        return Promise.resolve();
      }
      if (isEmpty(currentShopData.photos)) {
        return dispatch(addShopImage(shopId, avatar));
      }
      return dispatch(changeShopImage(shopId, currentShopData.photos[0], avatar));
    };
    const uploadLogo = () => {
      const logo = getMediaFile('logo');
      if (!logo) {
        return Promise.resolve();
      }
      return dispatch(uploadShopLogo(shopId, logo));
    };

    return Http.put(`/shop/${shopId}`, prepareData(data))
      .then(() => Promise.all([uploadImage(), uploadLogo()]))
      // API returns shop that doesn't have all the info so we fetch it manually to include what is needed
      .then(() => refreshShopData(shopId)(dispatch));
  };
}

export function userUpdateShop(shopId, data, currentShopData) {
  // remove restricted fields
  const restrictedFields = [
    'creditCardFeeEnabled',
    'creditCardFlatFee',
    'creditCardPercentFee',
    'presentCreditCardFlatFee',
    'presentCreditCardPercentFee',
    'customerBookingFee',
    'location',
    'bookNoPay',
    'kind',
    'enabled',
    'instantPayoutPercentageFee',
    'customerBookingFeeLimitEnabled',
    'customerBookingFeeBottomLimit',
    'customerBookingFeeTopLimit',
    'dailyPayrollTipFee',
    'commissionSplitsPercentFee',
  ];

  const forSave = omit(data, restrictedFields);
  return updateShop(shopId, forSave, currentShopData);
}

export function getShop(shopId, include) {
  const params = include || { include: 'address, photos, schedules, logo, days_off,brand' };

  return dispatch => Http.get(`/shop/${shopId}`, params).then((shop) => {
    dispatch({ type: GET_SHOP, shop });
    return shop;
  });
}

const fetchShopSubscriptionAction = () => ({ type: SHOP_FETCH_SUBSCRIPTION });

export const fetchShopSubscription = shopId => dispatch =>
  Http.get(`/shop/${shopId}/subscription`).then(tap(pipe(fetchShopSubscriptionAction, dispatch)));

export const fetchShopWithSubscription = (shopId) => async (dispatch) => {
  const shop = await dispatch(getShop(shopId));
  const subscription = await dispatch(fetchShopSubscription(shopId));
  const result = {
    ...shop,
    subscription,
  };
  return result;
};

export const fetchBrandShops = brandId => async (dispatch) => {
  dispatch(showLoading());

  return (
    Http.get(`/brand/${brandId}/shops`)
      .then(({ shops }) => {
        dispatch({
          type: GET_SHOPS,
          response: normalize(shops, schema.arrayOfShops),
        });
        dispatch(hideLoading());

        return shops;
      })
      .catch((err) => {
        dispatch(hideLoading());
        return err;
      })
  );
};
